import React from "react";
import { InteractiveContainer, PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import { CRMStatus, CRMStatusPill } from "../CRMStatusPill";
import { ReactComponent as ManageIcon } from "./assets/manage.svg";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";

export interface CRMAccountTileProps {
  title: string;
  icon: React.ReactNode;
  features: { icon: React.ReactNode; label: string }[];
  isConnected: boolean;
  status?: CRMStatus;
  isClickable?: boolean;
  pricingTooltip?: string;
  onClick?: () => void;
  connectLabel?: string;
}

export const CRMAccountTile: React.FC<CRMAccountTileProps> = ({
  title,
  icon,
  features,
  isConnected,
  status,
  isClickable,
  connectLabel,
  pricingTooltip,
  onClick,
}) => {
  const { t } = useTranslations();

  return (
    <InteractiveContainer
      className={cx(
        "flex h-95 w-79 max-w-96 grow flex-col justify-between rounded-3xl border border-solid border-dark-200 bg-white p-7 pt-8.5 transition-shadow duration-200 max-lg:h-auto max-lg:w-full max-lg:max-w-none max-lg:justify-start max-lg:gap-5 max-lg:p-5 max-lg:pt-6",
        isClickable && "hover:shadow-[0_4px_4px_0_rgba(0,0,0,0.08)]"
      )}
      onClick={onClick}
      isDisabled={!isClickable}
    >
      <div className="flex shrink-0 flex-col gap-5">
        {icon}
        <span className="truncate font-secondary text-lg leading-6.75 font-medium text-black">
          {title}
        </span>
        <ul className="flex flex-col gap-2 overflow-hidden max-lg:gap-3">
          {features
            .filter((feature) => !!feature.label)
            .map((feature, index) => (
              <li
                key={+index}
                className="flex items-center gap-2 font-secondary text-xs text-dark-700"
              >
                {feature.icon}
                <span className="truncate">{feature.label}</span>
              </li>
            ))}
        </ul>
      </div>
      <div className="flex shrink-0 items-center gap-3 max-lg:flex-col max-lg:items-stretch">
        {isConnected ? (
          <>
            {status && <CRMStatusPill status={status} size="md" />}
            <div
              className={cx(
                "flex h-8 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg border border-solid border-grey-300 font-secondary text-sm font-medium text-dark transition-[border-width] max-lg:flex-none",
                isClickable && "hover:border-2"
              )}
            >
              <ManageIcon className="shrink-0" />
              {t({ id: "crm-account-tile.manage", defaultMessage: "Manage" })}
            </div>
          </>
        ) : (
          <>
            {pricingTooltip && (
              <Tooltip
                renderTrigger={({ ref, props }) => (
                  <PlainButton
                    ref={ref}
                    {...props}
                    className={cx(
                      "flex h-8 w-8 items-center justify-center rounded-lg border border-solid border-grey-300 transition-[border-width]",
                      isClickable && "hover:border-2"
                    )}
                  >
                    <InfoIcon />
                  </PlainButton>
                )}
              >
                {pricingTooltip}
              </Tooltip>
            )}
            <div
              className={cx(
                "flex h-8 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg border border-solid border-grey-300 font-secondary text-sm font-medium text-dark transition-[border-width] max-lg:flex-none",
                isClickable && "hover:border-2"
              )}
            >
              {connectLabel ||
                t({
                  id: "crm-account-tile.connect",
                  defaultMessage: "Connect",
                })}
              {pricingTooltip ? <PricingIcon /> : <PlusIcon />}
            </div>
          </>
        )}
      </div>
    </InteractiveContainer>
  );
};
